import React, { useContext } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import rehypeSlug from "rehype-slug"
import { observer } from "mobx-react"
import { Anchor, Code, Table, Text, Title } from "@mantine/core"
import { CodeHighlight } from "@mantine/code-highlight"
import { AppWorkspaceContext } from "../stores/AppWorkspace"
import { CopyAnchorButton } from "./CopyAnchorButton"
import { codeLanguage } from "../../highlight"
import { SITE_PAGES } from "../helpers/constants"

// A platform guide's prompt (contract v8) holds one line per paragraph, so it
// pastes into a builder's chat without breaks mid-sentence. Its ```text block
// sits right after this marker; tag it so it wraps on screen, while the copy
// button still copies the text unchanged.
const PROMPT_FENCE = /(<!-- platform-prompt:begin -->\s*```)text\b/g
const PROMPT_LANGUAGE = "platform-prompt"

type MarkdownDocProps = {
  markdown: string
  className?: string
  copyAnchors?: boolean
}

type HeadingProps = {
  id?: string
  children?: React.ReactNode
}

const heading = (order: 1 | 2 | 3 | 4 | 5 | 6, copyAnchors: boolean) => {
  const Heading: React.FC<HeadingProps> = ({ id, children }) => (
    <Title
      order={Math.min(order, 4) as 1 | 2 | 3 | 4}
      id={id}
      className="or-heading"
      mb="sm"
      mt={order <= 2 ? "lg" : "md"}
    >
      <span className="or-heading-row">
        <span>{children}</span>
        {copyAnchors && id ? <CopyAnchorButton anchor={id} /> : null}
      </span>
    </Title>
  )
  Heading.displayName = `MarkdownH${order}`
  return Heading
}

export const MarkdownDoc: React.FC<MarkdownDocProps> = observer(
  ({ markdown, className, copyAnchors = true }) => {
    const workspace = useContext(AppWorkspaceContext)

    return (
      <div className={className ? `or-md ${className}` : "or-md"}>
        <Markdown
          remarkPlugins={[remarkGfm]}
          // rehype-raw first: the BTCPay README sizes its screenshots with raw
          // <img width=…> tags and the sync puts its demo in as a <video>,
          // neither of which markdown image syntax can say. The docs are the
          // library's own, rendered server-side by kramdown the same way.
          rehypePlugins={[rehypeRaw, rehypeSlug]}
          components={{
            h1: heading(1, copyAnchors),
            h2: heading(2, copyAnchors),
            h3: heading(3, copyAnchors),
            h4: heading(4, copyAnchors),
            p: ({ children }) => (
              <Text component="p" size="md" lh={1.7} mb="sm" c="dark.4">
                {children}
              </Text>
            ),
            a: ({ href, children }) => {
              const url = href ?? ""
              const internal =
                url.startsWith("/") || url.startsWith("#") || url.startsWith(workspace.publicOrigin)
              return (
                <Anchor
                  href={url}
                  onClick={(event) => {
                    if (!internal || !url) return
                    if (url.startsWith("#")) {
                      // On /api_docs a route links its wire types by anchor.
                      // While a search is active the target may be filtered
                      // out of the page, so clear the query, then select and
                      // scroll the way the table of contents does.
                      if (workspace.openPage !== SITE_PAGES.API_DOCS) return
                      event.preventDefault()
                      const anchor = url.slice(1)
                      if (!document.getElementById(anchor)) workspace.setDocsQuery("")
                      workspace.setHash(anchor)
                      requestAnimationFrame(() => {
                        document
                          .getElementById(anchor)
                          ?.scrollIntoView({ behavior: "smooth", block: "start" })
                      })
                      return
                    }
                    event.preventDefault()
                    workspace.openHref(url)
                  }}
                  target={internal ? undefined : "_blank"}
                  rel={internal ? undefined : "noopener noreferrer"}
                >
                  {children}
                </Anchor>
              )
            },
            img: ({ src, alt, width, height }) => (
              // Screenshots sit below the fold of a long README; the author's
              // width is kept, and CSS caps it at the column.
              <img
                src={src}
                alt={alt ?? ""}
                width={width}
                height={height}
                loading="lazy"
                decoding="async"
                className="or-md-img"
              />
            ),
            ul: ({ children }) => <ul className="or-md-list">{children}</ul>,
            ol: ({ children }) => <ol className="or-md-list">{children}</ol>,
            li: ({ children }) => <li>{children}</li>,
            table: ({ children }) => (
              <Table.ScrollContainer minWidth={520}>
                <Table striped highlightOnHover withTableBorder withColumnBorders mb="md">
                  {children}
                </Table>
              </Table.ScrollContainer>
            ),
            thead: ({ children }) => <Table.Thead>{children}</Table.Thead>,
            tbody: ({ children }) => <Table.Tbody>{children}</Table.Tbody>,
            tr: ({ children }) => <Table.Tr>{children}</Table.Tr>,
            th: ({ children }) => <Table.Th>{children}</Table.Th>,
            td: ({ children }) => <Table.Td>{children}</Table.Td>,
            code: ({ className: codeClass, children }) => {
              const language = /language-([a-z0-9_-]+)/i.exec(codeClass || "")?.[1]
              // A fenced block's text ends with a newline, inline code's never
              // does: a fence without a language is still a block, shown as text.
              const block = Boolean(language) || String(children).endsWith("\n")
              const text = String(children).replace(/\n$/, "")
              if (!block) {
                return (
                  <Code fz="sm" className="or-inline-code">
                    {text}
                  </Code>
                )
              }
              const prompt = language === PROMPT_LANGUAGE
              return (
                <CodeHighlight
                  code={text}
                  language={prompt || !language ? "text" : codeLanguage(language)}
                  className={prompt ? "or-prompt-code" : undefined}
                  withCopyButton
                  radius="md"
                  mb="md"
                />
              )
            },
            pre: ({ children }) => <>{children}</>
          }}
        >
          {markdown.replace(PROMPT_FENCE, `$1${PROMPT_LANGUAGE}`)}
        </Markdown>
      </div>
    )
  }
)
